import { useQuery } from '@tanstack/react-query'
import type { PaginationState } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { DataTable } from '@/shared/ui'

import { LEADS_PAGE_SIZES, leadsQueryFor } from '../api/queries'
import type { LeadSlice } from '../model/slice'

import { LeadsFilters } from './leads-filters'
import { leadColumns } from './leads-table-columns'

type LeadsTableProps = {
  campaignId: string
  slice: LeadSlice
  onSliceChange: (patch: Partial<LeadSlice>) => void
  page: number
  onPageChange: (page: number) => void
  pageSize: number
  onPageSizeChange: (pageSize: number) => void
  onSelect: (leadId: string) => void
}

/**
 * Server-paged, server-filtered log of one campaign's traffic.
 *
 * The window is not a nicety. `leads` gains a row per hit and is never pruned,
 * so both the page and the `total` under it have to be bounded by something —
 * and the period the caller is already looking at is that something.
 */
export function LeadsTable({
  campaignId,
  slice,
  onSliceChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onSelect,
}: LeadsTableProps) {
  const { t } = useTranslation('leads')

  // The same builder the route's loader calls, so the key it preloads is the
  // key this asks for — by construction rather than by inspection.
  const { data, isPending, error, refetch } = useQuery(
    leadsQueryFor(slice, { campaignId, page, pageSize }),
  )

  const leads = data?.items ?? []
  const columns = leadColumns(t)
  // «Ничего не найдено» versus «ничего нет». The period is excluded because it
  // is always set: counting it would make every empty table look filtered.
  const isFiltered =
    slice.q !== '' ||
    slice.type.length > 0 ||
    slice.served.length > 0 ||
    slice.country.length > 0 ||
    slice.source.length > 0

  const pagination: PaginationState = { pageIndex: page, pageSize }

  return (
    <DataTable
      data={leads}
      columns={columns}
      isPending={isPending}
      error={error}
      onRetry={() => void refetch()}
      // An empty page under a filter is not an empty log, and saying so would
      // send the reader looking for a bug instead of widening the slice.
      emptyTitle={isFiltered ? t('noMatch') : t('empty')}
      getRowId={(row) => row.id}
      onRowClick={(row) => onSelect(row.id)}
      pagination={pagination}
      onPaginationChange={(next) => {
        if (next.pageSize !== pageSize) onPageSizeChange(next.pageSize)
        onPageChange(next.pageIndex)
      }}
      pageSizeOptions={LEADS_PAGE_SIZES}
      rowCount={data?.total ?? 0}
      renderToolbar={() => (
        <LeadsFilters slice={slice} onSliceChange={onSliceChange} />
      )}
    />
  )
}
